import React, { useEffect, useMemo, useState } from 'react';
import { BaseIdWrapper, ExerciseFormat, Question, QuestionWithChildren } from '../../../../model/response';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store/store';
import { ColumnsType } from 'antd/es/table/interface';
import { message, Pagination, Popconfirm, Select, Table } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  findQuestionImportRequest,
  pageQuestions,
  softDeleteQuestion,
  toggleQuestionWebviewActive,
} from '../../../../service/questionService';
import processError from '../../../../utils/processError';
import { getEnumLabel, questionTypeList } from '../../../../utils/enumMapper';
import { getContentWithoutBreakLineStart, mapToSelectItems } from '../../../../utils/utils';
import { useMasterSubjectsSelector } from '../../../../redux/selector';
import { findAllExerciseFormats } from '../../../../service/exerciseFormatService';
import { QuestionType } from '../../../../model/enum';
import { showEditQuestionModal } from '../../../../redux/slice/editQuestionModalSlice';
import EditQuestionPopup from '../../../common/EditQuestionPopup';
import WebViewActiveCheckbox from '../../../common/WebViewActiveCheckbox';
import useTableConfig from '../../../../hooks/useTableConfig';
import Search from 'antd/es/input/Search';

function QuestionManagement() {
  const {
    pageSize,
    currentPage,
    sortBy,
    direction,
    pagination,
    setTotalElements,
    getColumnSorter,
    handlePaginationChange,
    handleSorterChange,
  } = useTableConfig();
  const [keyword, setKeyword] = useState<string>('');
  const [subjectId, setSubjectId] = useState<string>('');
  const [exerciseFormats, setExerciseFormats] = useState<ExerciseFormat[]>([]);
  const [exerciseFormatId, setExerciseFormatId] = useState<string>('');
  const [type, setType] = useState<QuestionType | ''>('');
  const [code, setCode] = useState('');
  const [webViewActive, setWebViewActive] = useState<boolean | ''>('');

  const [data, setData] = useState<QuestionWithChildren[]>([]);
  const [loading, setLoading] = useState(false);

  const subjects = useMasterSubjectsSelector();
  const dispatch = useDispatch<AppDispatch>();

  const loadListQuestions = async function () {
    try {
      setLoading(true);
      const response = await pageQuestions({
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        subjectId,
        exerciseFormatId,
        code,
        type,
        webViewActive,
      });
      setData(response.content);
      setTotalElements(response.totalElements);
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListQuestions().then();
  }, [currentPage, pageSize, sortBy, direction, subjectId, exerciseFormatId, keyword, code, type, webViewActive]);

  useEffect(() => {
    findAllExerciseFormats()
      .then((response) => setExerciseFormats(response))
      .catch(processError);
  }, []);

  useEffect(() => {
    setExerciseFormatId('');
  }, [subjectId]);

  const matchedExerciseFormats = useMemo(() => {
    if (!subjectId) return exerciseFormats;
    return exerciseFormats.filter((exerciseFormat) => exerciseFormat.subject?.id === subjectId);
  }, [subjectId, exerciseFormats]);

  const questionsWithoutChildren = useMemo(() => {
    const questions: Question[] = [];
    const processQuestions = (question: QuestionWithChildren, parent?: BaseIdWrapper) => {
      const { children, ...questionWithoutChildren } = question;
      questions.push({
        ...questionWithoutChildren,
        parent: parent as any,
      });
      children
        .sort((q1, q2) => q1.questionIndex - q2.questionIndex)
        .forEach((child) => processQuestions(child, { id: question.id }));
    };
    data.forEach((questionWithChildren) => processQuestions(questionWithChildren));
    return questions;
  }, [data]);

  async function handleEditQuestion(question: Question) {
    try {
      const questionImportRequest = await findQuestionImportRequest(question.id);
      dispatch(showEditQuestionModal(questionImportRequest));
    } catch (err) {
      processError(err);
    }
  }

  async function handleDeleteQuestion(question: Question) {
    try {
      setLoading(true);
      await softDeleteQuestion(question.id);
      await loadListQuestions();
      message.success('Xóa câu hỏi thành công');
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  }

  const columns: ColumnsType<Question> = [
    {
      title: 'Đề bài',
      dataIndex: 'topic',
      width: '60%',
      render: (_, record) => (
        <div
          className={record.parent ? 'ml-4' : ''}
          dangerouslySetInnerHTML={{
            __html: getContentWithoutBreakLineStart(record.topic),
          }}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('topic'),
    },
    {
      title: 'Mã',
      dataIndex: 'code',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('code'),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      align: 'center',
      render: (_, record) => getEnumLabel(record.type, questionTypeList),
      sorter: true,
      sortOrder: getColumnSorter('type'),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={'câu hỏi'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleQuestionWebviewActive(record.id)}
          reloadPageData={loadListQuestions}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('webViewActive'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handleEditQuestion(record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa câu hỏi"
            description="Xác nhận xóa câu hỏi. Hành động này không thể hoàn tác?"
            onConfirm={() => handleDeleteQuestion(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex gap-x-4 items-center justify-between bg-white rounded">
        <Search
          placeholder={'Bộ lọc - Từ khóa'}
          rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
          onSearch={setKeyword}
        />
        <Search
          placeholder={'Bộ lọc - Mã câu hỏi'}
          rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
          onSearch={setCode}
        />
        <Select
          onChange={(value) => setSubjectId(value)}
          className="w-[240px]"
          placeholder="Bộ lọc - Môn học"
          style={{ flex: 1 }}
          value={subjectId}
          options={[
            {
              value: '',
              label: 'Môn học - Tất cả',
            },
          ].concat(mapToSelectItems(subjects))}
        />
        <Select
          onChange={(value) => setExerciseFormatId(value)}
          className="w-[240px]"
          placeholder="Bộ lọc - Dạng bài tập"
          style={{ flex: 1 }}
          value={exerciseFormatId}
          options={[
            {
              value: '',
              label: 'Dạng bài tập - Tất cả',
            },
          ].concat(mapToSelectItems(matchedExerciseFormats))}
        />
        <Select
          onChange={(value) => setType(value)}
          className="w-[240px]"
          placeholder="Bộ lọc - Loại câu hỏi"
          style={{ flex: 1 }}
          value={type}
          options={[
            {
              value: '',
              label: 'Loại câu hỏi - Tất cả',
            },
          ].concat(questionTypeList)}
        />
        <Select
          onChange={(value) => setWebViewActive(value)}
          className="w-[240px]"
          placeholder="Trạng thái hiển thị"
          style={{ flex: 1 }}
          value={webViewActive}
          options={[
            {
              value: '',
              label: 'Trạng thái - Tất cả',
            },
            {
              value: true,
              label: 'Hiển thị trên web',
            },
            {
              value: false,
              label: 'Ẩn trên web',
            },
          ]}
        />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={questionsWithoutChildren}
          onChange={(_, __, sorter) => handleSorterChange(sorter)}
          pagination={false}
          loading={loading}
        />
        <Pagination rootClassName={'w-fit ml-auto mt-4'} {...pagination} onChange={handlePaginationChange} />
      </div>
      <EditQuestionPopup onBeforeSuccess={loadListQuestions} />
    </div>
  );
}

export default QuestionManagement;
